/**
 * Kolejność kroków wylogowania — wyodrębniona, żeby dała się sprawdzić testem.
 *
 * WADA, KTÓRĄ TO NAPRAWIA. Gałąź `SIGNED_OUT` w `onAuthStateChange` robiła
 * `await resetSession(...)` PRZED `clearLocalAuthState()`. Dwie osobne rzeczy
 * szły przez to źle:
 *
 *  1. BLOKADA GoTrue. Komentarz osiem linii wyżej, przy gałęzi `SIGNED_IN`,
 *     mówi wprost: „Awaiting Supabase calls directly inside this callback can
 *     deadlock on the GoTrue auth lock that is held for the callback's
 *     duration" — i dlatego tamta gałąź odracza pracę przez `setTimeout(0)`.
 *     Gałąź wylogowania tego nie robiła, a `resetSession` woła
 *     `await supabase.removeAllChannels()`, czyli round-trip po websocketach.
 *
 *  2. INTERFEJS KŁAMAŁ O SESJI. Dopóki `clearLocalAuthState()` nie pobiegło,
 *     `isAuthenticated` zostawało prawdą, więc `ProtectedRoute` renderował
 *     dalej treść chronioną zamiast przekierować na /login. Użytkownik był już
 *     wylogowany, a aplikacja twierdziła, że nie — przez czas sprzątania kanałów
 *     realtime, którego nikt nie ogranicza.
 *
 * Objaw był widoczny w CI: `auth.spec.ts › should logout successfully` bywał
 * `flaky`, bo asertuje `toHaveURL(/\/login/)` z domyślnym limitem 5 s. Testy
 * w `session-isolation.spec.ts` tego nie łapały, bo ich pomocnik `signOut()`
 * odpytuje w pętli przez 15 s — czekał więc na wadę zamiast ją pokazać.
 *
 * KOLEJNOŚĆ JEST TERAZ ODWROTNA I TO JEST CAŁA POPRAWKA: najpierw prawda
 * o sesji (stan lokalny + rozgłoszenie do innych kart), potem sprzątanie —
 * odroczone poza callback, żeby nie trzymać blokady.
 *
 * Ten sam objaw miał drugą, niezależną przyczynę (15.10.2026): odpowiedź
 * `fetchUserData`, która przyszła PO wylogowaniu, wskrzeszała użytkownika.
 * Zamyka ją pokolenie sesji w `AuthProvider` (`pokolenieSesjiRef`), a mierzy
 * `tests/unit/apps/wylogowanieWTrakcieWczytywania.test.tsx`.
 */

export interface SignOutSequenceDeps {
  /** Czyści lokalny stan Reacta. Synchroniczne, bez wejścia do sieci. */
  clearLocalAuthState: () => void;
  /** Rozgłasza wylogowanie do pozostałych kart. */
  broadcastSignedOut: () => void;
  /** Ciężkie sprzątanie: kanały realtime, magazyny, localStorage, iframe'y. */
  teardown: () => Promise<void>;
  /**
   * Wypycha pracę poza bieżący callback. W produkcji `setTimeout(fn, 0)`;
   * w teście funkcja, która pozwala sprawdzić, CO zdążyło się wykonać wcześniej.
   */
  defer: (fn: () => void) => void;
  /** Zgłasza błąd sprzątania. Sprzątanie nie może wywrócić wylogowania. */
  onTeardownError?: (error: unknown) => void;
}

export function runSignOutSequence(deps: SignOutSequenceDeps): void {
  // 1. Użytkownik JEST wylogowany w tej chwili — interfejs ma to odzwierciedlić
  //    natychmiast, zanim cokolwiek dotknie sieci.
  deps.clearLocalAuthState();
  deps.broadcastSignedOut();

  // 2. Sprzątanie poza callbackiem. Jego błąd jest logowany, ale NIE cofa
  //    wylogowania: sesja już nie istnieje, więc udawanie, że istnieje, byłoby
  //    gorsze niż nieposprzątany kanał.
  deps.defer(() => {
    void deps.teardown().catch((error) => deps.onTeardownError?.(error));
  });
}
